import { FC, useCallback, useState } from "react";

import { SectionRole } from "../../../../shared/types/roles";

import { LateSubmissionCard } from "./LateSubmissionCard";

import { AssignedPagesList } from "components/materials/AssignedPagesList";
import { MoreItemsButton } from "components/materials/MoreItemsButton";
import { ReadyForAssessmentPagesList } from "components/materials/ReadyForAssessmentPagesList";
import { RecentlyAssessedPagesList } from "components/materials/RecentlyAssessedPagesList";
import { getMaxItemsPerSection } from "components/materials/helper";
import { t } from "i18n/i18n";
import { HorizontalDivider } from "mds/components/HorizontalDivider";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useCapabilities } from "store/capability-hooks";

type PlannerSectionCountType = {
  due: number;
  late: number;
  assess: number;
};

type PlannerProps = {
  shouldLinkSidebar?: boolean;
  showAll?: boolean;
  role?: SectionRole;
};

export const Planner: FC<PlannerProps> = ({ shouldLinkSidebar, showAll: showAllAlways, role }) => {
  const [sectionCounts, setSectionCounts] = useState<PlannerSectionCountType>(
    {} as PlannerSectionCountType,
  );
  const [showAll, setShowAll] = useState<boolean>(!!showAllAlways);
  const { canAuthorSection, canParticipateInSection: isStudent } = useCapabilities();
  const hasRole = Boolean(role);
  const maxItems = getMaxItemsPerSection(hasRole);
  const moreThanMaxItems = Object.values(sectionCounts).some((count) => count > maxItems);

  const setSectionCount = useCallback((section: keyof PlannerSectionCountType, count: number) => {
    setSectionCounts((prev) => ({ ...prev, [section]: count }));
  }, []);

  const shouldShowLinkButton = (section: keyof PlannerSectionCountType) => {
    const count = sectionCounts[section];
    return count !== undefined && count > maxItems && !showAll && !showAllAlways;
  };

  const handleDueCountChanged = useCallback(
    (count: number) => setSectionCount("due", count),
    [setSectionCount],
  );

  const handleAssessCountChanged = useCallback(
    (count: number) => setSectionCount("assess", count),
    [setSectionCount],
  );

  return (
    <>
      {!isStudent && canAuthorSection && (
        <>
          <ReadyForAssessmentPagesList
            role={role}
            shouldLinkSidebar={shouldLinkSidebar}
            showAll={showAll}
            onCountChanged={handleAssessCountChanged}
          />

          {shouldShowLinkButton("assess") && (
            <MoreItemsButton
              count={sectionCounts.assess}
              maxItems={maxItems}
              onClick={() => setShowAll(true)}
            />
          )}

          <LateSubmissionCard />

          <HorizontalDivider className="mb-4 mt-6" />
        </>
      )}

      <div className="mb-3">
        <AssignedPagesList
          category="all"
          role={role}
          shouldLinkSidebar={shouldLinkSidebar}
          showAll={showAll}
          timeframe="open-for-submissions"
          onCountChanged={handleDueCountChanged}
        />
      </div>

      {shouldShowLinkButton("due") && (
        <MoreItemsButton
          count={sectionCounts.due}
          maxItems={maxItems}
          onClick={() => setShowAll(true)}
        />
      )}

      {isStudent && (
        <>
          <HorizontalDivider className="mb-4 mt-6" />
          <RecentlyAssessedPagesList
            role={role}
            shouldLinkSidebar={shouldLinkSidebar}
            showAll={showAll}
            onCountChanged={handleAssessCountChanged}
          />
        </>
      )}

      {moreThanMaxItems && !showAllAlways && (
        <SquareButton
          className="mb-3 mt-1"
          kind="tertiary"
          size="s"
          onClick={() => setShowAll((prev) => !prev)}
        >
          {showAll ? t("common.view_less") : t("common.view_all")}
        </SquareButton>
      )}
    </>
  );
};
